/**
 * ServiceOrderStockDialog - Seleção de estoque ao concluir a OS (Fase 9)
 *
 * Instalação: itens ativos no estoque do prestador → cliente/veículo.
 * Desinstalação: itens no veículo da OS → estoque do prestador.
 * Troca: os dois lados obrigatórios (BR-OS-009). O servidor revalida tudo.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-10 v1.4.0 - BR-OS-034..037: modo correção (OS "Em correção"):
 *            escolha entre "Manter equipamentos" (sem movimentação) e "Corrigir
 *            equipamento" (devolve item desta OS e escolhe o correto), com motivo.
 *   RELEASE: 2026-10-10 v1.3.2 - Selo de situação operacional ("Instalado"/"Ativo"/
 *            "Disponível") passa a verde com o token `success`; indisponível segue vermelho.
 *   RELEASE: 2026-10-09 v1.3.1 - Restaura no cartão do kit os quadros compactos de
 *            rastreador (situação, IMEI, modelo) e chip (situação, ICCID, plano).
 *   RELEASE: 2026-10-09 v1.3.0 - Cartão do kit compacto (remove detalhes de rastreador/chip),
 *            ícone do kit sem moldura e colorido, situação do kit exibida como
 *            "Disponível"/"Indisponível" (somente front), rolagem corrigida no mobile
 *            (container de rolagem com flex-1 e TabsContent em flex-col) e efeito dos
 *            cartões por elevação (sombra) em vez de troca de cor da borda.
 *   RELEASE: 2026-10-09 v1.2.0 - Confirmação adicional quando houver mais de um kit
 *            selecionado no mesmo lado (instalação/remoção), evitando erro do prestador.
 *   RELEASE: 2026-10-09 v1.1.0 - Redesign premium com cartões detalhados,
 *            elegibilidade dos componentes e abas Remover/Instalar na troca.
 *   RELEASE: 2026-10-08 v1.0.0 - Criação inicial.

 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Package, Search, ShieldCheck, Undo2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { StockCandidateCard, sidesWithMultipleKits } from './StockCandidateCard';
export { sidesWithMultipleKits } from './StockCandidateCard';
const key = (c: StockCandidate) => `${c.item_type}:${c.item_id}`;
import { useServiceOrderStockCandidates, useServiceOrderStockRealtime, pruneSelection, validateCorrectionSelection, validateStockSelection,
  type CorrectionMode, type ServiceOrderStockSelection, type StockCandidate } from '@/hooks/useServiceOrderStock';

interface Props {
  serviceOrderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saving: boolean;
  /** `note` só é enviado na correção (motivo obrigatório quando há troca). */
  onConfirm: (stock: ServiceOrderStockSelection | null, note?: string) => void;
}

function ItemList({ items, selected, onToggle, emptyTitle, emptyDescription }: {

  items: StockCandidate[]; selected: Set<string>; onToggle: (k: string) => void;
  emptyTitle: string; emptyDescription: string;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();
  const shown = items.filter((i) => `${i.label} ${i.detail} ${i.tracker_imei ?? ''} ${i.tracker_model_name ?? ''} ${i.chip_iccid ?? ''} ${i.chip_line_number ?? ''} ${i.chip_data_plan_name ?? ''}`.toLowerCase().includes(term));
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {items.length > 3 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" value={search} maxLength={60} placeholder="Buscar kit, IMEI, modelo, linha ou plano…"
            onChange={(e) => setSearch(e.target.value.slice(0, 60))} />
        </div>
      )}
      {/* flex-1 garante altura limitada ao espaço restante: sem isso a lista cresce
          além do modal e não rola no mobile. overscroll-contain evita rolar a página. */}
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto overscroll-contain pr-1">

        {shown.length === 0 ? (
          <div className="flex min-h-52 flex-col items-center justify-center px-6 text-center">
            <AlertCircle className="mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-semibold text-foreground">{emptyTitle}</p>
            <p className="mt-1 max-w-sm text-xs text-muted-foreground">{emptyDescription}</p>
          </div>
        ) : shown.map((item) => (
          <StockCandidateCard key={key(item)} item={item} checked={selected.has(key(item))}
            onToggle={() => item.eligible && onToggle(key(item))} />
        ))}
      </div>
    </div>
  );
}

export function ServiceOrderStockDialog({ serviceOrderId, open, onOpenChange, saving, onConfirm }: Props) {
  const { data, isLoading, error } = useServiceOrderStockCandidates(serviceOrderId, open);
  // Atualização automática: Realtime enquanto aberto (além de reconsulta ao abrir e a cada 15s)
  useServiceOrderStockRealtime(serviceOrderId, open);
  const [install, setInstall] = useState<Set<string>>(new Set());
  const [uninstall, setUninstall] = useState<Set<string>>(new Set());
  // v1.4.0: modo correção — itens desta OS a desfazer + motivo
  const [mode, setMode] = useState<CorrectionMode>('keep');
  const [revertInstall, setRevertInstall] = useState<Set<string>>(new Set());
  const [revertUninstall, setRevertUninstall] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setInstall(new Set()); setUninstall(new Set());
      setMode('keep'); setRevertInstall(new Set()); setRevertUninstall(new Set()); setReason('');
    }
  }, [open]);
  const isCorrection = !!data?.correction;
  // Lista atualizada: desmarca itens que deixaram de estar disponíveis
  useEffect(() => {
    if (!data) return;
    setInstall((s) => pruneSelection(s, data.correction ? data.correction_install : data.install));
    setUninstall((s) => pruneSelection(s, data.uninstall));
    setRevertInstall((s) => pruneSelection(s, data.correction_remove, false));
    setRevertUninstall((s) => pruneSelection(s, data.revert_uninstall, false));
  }, [data]);

  const toggle = (set: Set<string>, upd: (s: Set<string>) => void) => (k: string) => {
    const next = new Set(set); next.has(k) ? next.delete(k) : next.add(k); upd(next);
  };
  const toRefs = (s: Set<string>) => [...s].map((k) => { const [type, id] = k.split(':'); return { type, id } as any; });
  const selection: ServiceOrderStockSelection = useMemo(() => ({
    install: toRefs(install), uninstall: toRefs(uninstall),
    revert_install: toRefs(revertInstall), revert_uninstall: toRefs(revertUninstall),
  }), [install, uninstall, revertInstall, revertUninstall]);
  const problem = !data ? null
    : isCorrection ? validateCorrectionSelection(data, mode, selection, reason)
    : validateStockSelection(data, selection);
  const showInstall = data && ['install', 'swap'].includes(data.movement);
  const showUninstall = data && ['uninstall', 'swap'].includes(data.movement);
  const [activeSide, setActiveSide] = useState<'uninstall' | 'install'>('uninstall');

  useEffect(() => {
    if (open) setActiveSide('uninstall');
  }, [open]);

  // Confirmação extra: pop-up obrigatório quando houver mais de um kit no mesmo lado.
  const [confirmOpen, setConfirmOpen] = useState(false);
  useEffect(() => { if (open) setConfirmOpen(false); }, [open]);
  const multiKitSides = useMemo(() => sidesWithMultipleKits([...install], [...uninstall]), [install, uninstall]);
  const handleConfirm = () => {
    // Correção: "Manter" conclui sem movimentar; "Corrigir" envia as listas e o motivo
    if (isCorrection) {
      if (mode === 'keep') return onConfirm(null);
      return onConfirm(selection, reason.trim().slice(0, 1000));
    }
    if (multiKitSides.length > 0) {
      setConfirmOpen(true);
      return;
    }
    onConfirm(selection.install.length + selection.uninstall.length > 0 ? selection : null);
  };
  // Descrição dinâmica do pop-up, listando apenas os lados com múltiplos kits.
  const multiKitDescription = useMemo(() => {
    const kitCount = (s: Set<string>) => [...s].filter((k) => k.startsWith('kit:')).length;
    const parts: string[] = [];
    if (kitCount(install) > 1) parts.push(`${kitCount(install)} kits para instalar no veículo`);
    if (kitCount(uninstall) > 1) parts.push(`${kitCount(uninstall)} kits para remover do veículo`);
    return `Você selecionou ${parts.join(' e ')}. Confirma que essa é a seleção correta antes de concluir a OS?`;
  }, [install, uninstall]);

  const uninstallList = data ? (
    <ItemList items={data.uninstall} selected={uninstall} onToggle={toggle(uninstall, setUninstall)}
      emptyTitle="Nenhum item instalado"
      emptyDescription="Este veículo não possui equipamentos disponíveis para remover." />
  ) : null;
  const installList = data ? (
    <ItemList items={data.install} selected={install} onToggle={toggle(install, setInstall)}
      emptyTitle="Nenhum item disponível"
      emptyDescription="O prestador não possui equipamentos disponíveis para instalar." />
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent enableSwipeToClose={false} className="flex h-[92dvh] max-h-[820px] w-[calc(100vw-1rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:w-full">
        <DialogHeader className="shrink-0 border-b px-5 py-4 pr-12">
          <DialogTitle className="flex items-center gap-2 text-base"><Package className="h-5 w-5 text-primary" /> Equipamentos utilizados</DialogTitle>
          <DialogDescription>Selecione somente os equipamentos usados nesta ordem de serviço.</DialogDescription>
        </DialogHeader>
        {isLoading ? <Skeleton className="h-40" /> : error ? (
          <div className="p-5"><InlineNotice variant="error">{(error as Error).message}</InlineNotice></div>
        ) : data && (
          <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-5">
            {isCorrection && (
              <div className="mb-4 shrink-0 space-y-3">
                <InlineNotice variant="info">
                  Esta OS está em correção e já movimentou equipamentos. Escolha se eles permanecem como estão ou se algum precisa ser corrigido.
                </InlineNotice>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { v: 'keep', icon: ShieldCheck, t: 'Manter equipamentos', d: 'Nada muda no estoque' },
                    { v: 'fix', icon: Undo2, t: 'Corrigir equipamento', d: 'Trocar item escolhido errado' },
                  ] as const).map((o) => (
                    <button key={o.v} type="button" onClick={() => setMode(o.v)}
                      className={cn('flex items-start gap-2 rounded-md border p-3 text-left transition-all',
                        mode === o.v ? 'border-primary bg-primary/5 shadow-sm' : 'hover:shadow-sm')}>
                      <o.icon className={cn('mt-0.5 h-4 w-4 shrink-0', mode === o.v ? 'text-primary' : 'text-muted-foreground')} />
                      <span><span className="block text-sm font-semibold">{o.t}</span><span className="block text-[11px] text-muted-foreground">{o.d}</span></span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {isCorrection && mode === 'keep' ? null : isCorrection ? (
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1">
                {/* v1.5.0 (BR-OS-038): Corrigir = troca — remove do veículo e instala do prestador */}
                <Tabs defaultValue="remove" className="flex flex-col">
                  <TabsList className="mb-3 grid h-auto w-full grid-cols-2 gap-1 p-1">
                    <TabsTrigger value="remove" className="gap-2 py-2.5">
                      <Package className="h-4 w-4 text-destructive" /> Remover do veículo ({revertInstall.size})
                    </TabsTrigger>
                    <TabsTrigger value="install" className="gap-2 py-2.5">
                      <Package className="h-4 w-4 text-primary" /> Instalar ({install.size})
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="remove" className="mt-0">
                    <p className="mb-2 text-xs text-muted-foreground">Marque o equipamento escolhido errado. Ele volta ao estoque do prestador como Ativo.</p>
                    <ItemList items={(data.correction_remove ?? []).map((i) => ({ ...i, eligible: true }))} selected={revertInstall}
                      onToggle={toggle(revertInstall, setRevertInstall)}
                      emptyTitle="Nenhum kit no veículo" emptyDescription="Basta escolher o equipamento correto na aba Instalar." />
                  </TabsContent>
                  <TabsContent value="install" className="mt-0">
                    <p className="mb-2 text-xs text-muted-foreground">Marque o equipamento correto do estoque do prestador. Ele será instalado no veículo da OS.</p>
                    <ItemList items={data.correction_install ?? []} selected={install} onToggle={toggle(install, setInstall)}
                      emptyTitle="Nenhum item disponível" emptyDescription="O prestador não possui equipamentos disponíveis para instalar." />
                  </TabsContent>
                </Tabs>
                <div>
                  <p className="mb-2 text-sm font-semibold">Motivo da correção <span className="text-destructive">*</span></p>
                  <Textarea value={reason} maxLength={1000} rows={3} placeholder="Explique o que foi corrigido…"
                    onChange={(e) => setReason(e.target.value.replace(/[<>]/g, '').slice(0, 1000))} />
                </div>
              </div>
            ) : (<>
            {!data.has_vehicle && <InlineNotice variant="warning">A OS não tem veículo; não é possível movimentar estoque.</InlineNotice>}
            {data.movement === 'swap' ? (
              <Tabs value={activeSide} onValueChange={(value) => setActiveSide(value as 'uninstall' | 'install')} className="flex min-h-0 flex-1 flex-col">
                <TabsList className="mb-4 grid h-auto w-full shrink-0 grid-cols-2 gap-1 p-1">
                  <TabsTrigger value="uninstall" className="gap-2 py-2.5">
                    <Package className="h-4 w-4 text-destructive" /> Remover ({uninstall.size})
                  </TabsTrigger>
                  <TabsTrigger value="install" className="gap-2 py-2.5">
                    <Package className="h-4 w-4 text-primary" /> Instalar ({install.size})
                  </TabsTrigger>
                </TabsList>
                {/* flex-col é obrigatório: o conteúdo do Tabs é bloco por padrão e, sem ele,
                    o flex-1 da lista interna não limita a altura (quebra a rolagem no mobile). */}
                <TabsContent value="uninstall" className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">{uninstallList}</TabsContent>
                <TabsContent value="install" className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">{installList}</TabsContent>

              </Tabs>
            ) : showUninstall ? (
              <div className="flex min-h-0 flex-1 flex-col">
                <p className="mb-3 text-sm font-semibold">Remover do veículo <span className="font-normal text-muted-foreground">({uninstall.size} selecionado(s))</span></p>
                {uninstallList}
              </div>
            ) : showInstall ? (
              <div className="flex min-h-0 flex-1 flex-col">
                <p className="mb-3 text-sm font-semibold">Instalar no veículo <span className="font-normal text-muted-foreground">({install.size} selecionado(s))</span></p>
                {installList}
              </div>
            ) : null}
            </>)}
          </div>
        )}
        <DialogFooter className="shrink-0 border-t bg-muted/20 px-5 py-4 sm:items-center sm:justify-between">
          <div className="flex min-h-5 items-center gap-2 text-xs">
            {problem ? <><AlertCircle className="h-4 w-4 text-destructive" /><span className="font-medium text-destructive">{problem}</span></>
              : <><CheckCircle2 className="h-4 w-4 text-primary" /><span className="text-muted-foreground">Seleção pronta para confirmar</span></>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!data || !!problem || saving} onClick={handleConfirm}>
              {isCorrection ? (mode === 'keep' ? 'Concluir sem alterar' : 'Corrigir e concluir')
                : data?.movement === 'swap' ? 'Confirmar troca' : 'Confirmar e concluir'}
          </Button>
          </div>
        </DialogFooter>

        {/* Pop-up de confirmação quando há mais de um kit no mesmo lado (BR-OS-019) */}
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          variant="info"
          title="Confirmar seleção de múltiplos kits"
          description={multiKitDescription}
          icon={<Package className="h-6 w-6" />}
          confirmText="Confirmar seleção"
          isLoading={saving}
          onConfirm={() => { setConfirmOpen(false); onConfirm(selection); }}
        />
      </DialogContent>
    </Dialog>
  );
}
